import { useTranslation } from 'react-i18next';

import { classNames } from '@utils/classNames';
import { IconType } from '@src/types/IconType';
import { Icon } from '@components/atoms/Icon';
import { ParameterStatusType } from '@components/atoms/ParameterStatusType.ts';

export type ParameterStatusProps = {
  status: ParameterStatusType;
  className?: string;
  hasCircle?: boolean;
};

const statusConfig = {
  error: {
    bgColor: 'bg-red/10',
    solidBgColor: 'bg-red',
    textColor: 'text-red',
    borderColor: 'border-red',
    icon: IconType.ExclamationCircle,
  },
  warning: {
    bgColor: 'bg-orange/10',
    solidBgColor: 'bg-orange',
    textColor: 'text-orange',
    borderColor: 'border-orange',
    icon: IconType.ExclamationTriangle,
  },
  success: {
    bgColor: 'bg-green/10',
    solidBgColor: 'bg-green',
    textColor: 'text-green',
    borderColor: 'border-green',
    icon: IconType.Checkmark,
  },
  unset: {
    bgColor: 'bg-grey3light/10',
    solidBgColor: 'bg-grey3light',
    textColor: 'text-grey3light',
    borderColor: 'border-grey3light',
    icon: IconType.Checkmark,
  },
  notAnalyzable: {
    bgColor: 'bg-black/10',
    solidBgColor: 'bg-black',
    textColor: 'text-black',
    borderColor: 'border-black',
    icon: IconType.Checkmark,
  },
};

export const ParameterStatus = ({
  status,
  className,
  hasCircle = false,
}: ParameterStatusProps) => {
  const { t } = useTranslation('component', {
    keyPrefix: 'parameterList.statusTypes',
  });
  const config = statusConfig[status];
  const statusText = t(status);

  return (
    <div data-testid="parameter-status">
      <div className="md:invisible md:w-0 md:h-0 print:invisible print:w-0 print:h-0">
        <div
          className={classNames(
            'h-8 w-11 flex items-center justify-center border-2 rounded-bonusLarge',
            config.borderColor,
            config.bgColor,
          )}
        >
          <div
            className={classNames(
              'rounded-full flex items-center justify-center',
              className,
            )}
          >
            <Icon
              type={config.icon}
              className={classNames('w-[18px] h-[18px]', config.textColor)}
            />
          </div>
        </div>
      </div>
      <div className="hidden md:block print:block">
        <div
          className={classNames(
            'border-2 px-3 py-1 rounded-bonusLarge flex items-center gap-2',
            config.borderColor,
            config.bgColor,
          )}
        >
          {hasCircle && (
            <div
              className={classNames(
                'w-2 h-2 rounded-full',
                config.solidBgColor,
              )}
            />
          )}
          <span
            className={classNames(
              'text-sm font-normal font-outfit leading-normal',
              config.textColor,
            )}
          >
            {statusText}
          </span>
        </div>
      </div>
    </div>
  );
};
